/**
 * 审批工作流管理页面
 */

import React, { useState } from 'react';
import { 
  Card, 
  Button, 
  Space, 
  Tag, 
  Modal, 
  message,
  Tooltip,
  Dropdown,
  Descriptions,
  Form,
  Input,
  Select,
  Switch,
  Row,
  Col,
  Statistic,
  Steps,
  Tree,
  type MenuProps
} from 'antd';
import { modal } from '@/utils/antdStatic';
import {
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined,
  EyeOutlined,
  MoreOutlined,
  ReloadOutlined,
  ExportOutlined,
  SettingOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  CopyOutlined,
  NodeIndexOutlined,
  BranchesOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '@/hooks/useI18n';
import AdvancedProTable from '@/components/common/ProTable/AdvancedProTable';
import { SearchForm } from '@/components/common/SearchForm';
import type { ProTableColumn, ProTableInstance } from '@/components/common/ProTable/AdvancedProTable';

const { Option } = Select;
const { TextArea } = Input;

// 工作流状态枚举
enum WorkflowStatus {
  DRAFT = 'draft',
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  ARCHIVED = 'archived'
}

// 节点类型枚举
enum NodeType {
  START = 'start',
  END = 'end',
  APPROVAL = 'approval',
  CONDITION = 'condition',
  PARALLEL = 'parallel',
  MERGE = 'merge'
}

interface WorkflowNode {
  id: string;
  name: string;
  type: NodeType;
  approverType: 'user' | 'role' | 'department';
  approverIds: string[];
  approverNames: string[];
  approvalType: 'any' | 'all' | 'sequence';
  timeLimit?: number;
  conditions?: string;
  x: number;
  y: number;
}

interface Workflow {
  id: string;
  name: string;
  code: string;
  description: string;
  category: string;
  status: WorkflowStatus;
  version: string;
  isDefault: boolean;
  nodes: WorkflowNode[];
  creatorId: string;
  creatorName: string;
  instanceCount: number;
  activeInstanceCount: number;
  avgProcessTime: number;
  createdAt: string;
  updatedAt: string;
}

interface WorkflowQueryParams {
  current?: number;
  pageSize?: number;
  name?: string;
  code?: string;
  category?: string;
  status?: WorkflowStatus;
  creatorId?: string;
}

const WorkflowList: React.FC = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [searchParams, setSearchParams] = useState<WorkflowQueryParams>({});
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [designModalVisible, setDesignModalVisible] = useState(false);
  const [currentWorkflow, setCurrentWorkflow] = useState<Workflow | null>(null);
  const [workflowStats, setWorkflowStats] = useState({
    totalWorkflows: 0,
    activeWorkflows: 0,
    totalInstances: 0,
    avgProcessTime: 0,
  });
  const tableRef = React.useRef<ProTableInstance>(null);

  // 模拟获取工作流数据
  const fetchWorkflowList = async (params: any) => {
    setLoading(true);
    try {
      // 模拟数据
      const mockData: Workflow[] = [
        {
          id: 'wf-1',
          name: '销售订单审批流程',
          code: 'SALES_ORDER_APPROVAL',
          description: '销售订单金额大于10万元时需要经过销售总监和财务总监审批',
          category: '订单管理',
          status: WorkflowStatus.ACTIVE,
          version: 'v1.2',
          isDefault: true,
          nodes: [
            {
              id: 'node-1',
              name: '开始',
              type: NodeType.START,
              approverType: 'user',
              approverIds: [],
              approverNames: [],
              approvalType: 'any',
              x: 100,
              y: 100,
            },
            {
              id: 'node-2',
              name: '销售经理审批',
              type: NodeType.APPROVAL,
              approverType: 'role',
              approverIds: ['role-sales-manager'],
              approverNames: ['销售经理'],
              approvalType: 'any',
              timeLimit: 24,
              x: 300,
              y: 100,
            },
            {
              id: 'node-3',
              name: '金额判断',
              type: NodeType.CONDITION,
              approverType: 'user',
              approverIds: [],
              approverNames: [],
              approvalType: 'any',
              conditions: 'totalAmount > 100000',
              x: 500,
              y: 100,
            },
            {
              id: 'node-4',
              name: '财务总监审批',
              type: NodeType.APPROVAL,
              approverType: 'role',
              approverIds: ['role-finance-director'],
              approverNames: ['财务总监'],
              approvalType: 'any',
              timeLimit: 48,
              x: 700,
              y: 100,
            },
            {
              id: 'node-5',
              name: '结束',
              type: NodeType.END,
              approverType: 'user',
              approverIds: [],
              approverNames: [],
              approvalType: 'any',
              x: 900,
              y: 100,
            },
          ],
          creatorId: 'user-1',
          creatorName: '系统管理员',
          instanceCount: 156,
          activeInstanceCount: 8,
          avgProcessTime: 18.5,
          createdAt: '2024-01-01 10:00:00',
          updatedAt: '2024-12-15 15:30:00',
        },
        {
          id: 'wf-2',
          name: '采购申请审批流程',
          code: 'PURCHASE_REQUEST_APPROVAL',
          description: '采购申请需要经过部门负责人和采购总监审批',
          category: '采购管理',
          status: WorkflowStatus.ACTIVE,
          version: 'v1.0',
          isDefault: false,
          nodes: [
            {
              id: 'node-1',
              name: '开始',
              type: NodeType.START,
              approverType: 'user',
              approverIds: [],
              approverNames: [],
              approvalType: 'any',
              x: 100,
              y: 100,
            },
            {
              id: 'node-2',
              name: '部门负责人审批',
              type: NodeType.APPROVAL,
              approverType: 'department',
              approverIds: ['dept-head'],
              approverNames: ['部门负责人'],
              approvalType: 'any',
              timeLimit: 24,
              x: 300,
              y: 100,
            },
            {
              id: 'node-3',
              name: '采购总监审批',
              type: NodeType.APPROVAL,
              approverType: 'role',
              approverIds: ['role-purchase-director'],
              approverNames: ['采购总监'],
              approvalType: 'any',
              timeLimit: 48,
              x: 500,
              y: 100,
            },
            {
              id: 'node-4',
              name: '结束',
              type: NodeType.END,
              approverType: 'user',
              approverIds: [],
              approverNames: [],
              approvalType: 'any',
              x: 700,
              y: 100,
            },
          ],
          creatorId: 'user-2',
          creatorName: '采购管理员',
          instanceCount: 89,
          activeInstanceCount: 3,
          avgProcessTime: 12.3,
          createdAt: '2024-02-01 10:00:00',
          updatedAt: '2024-12-10 14:20:00',
        },
      ];

      // 更新统计数据
      setWorkflowStats({
        totalWorkflows: mockData.length,
        activeWorkflows: mockData.filter(item => item.status === WorkflowStatus.ACTIVE).length,
        totalInstances: mockData.reduce((sum, item) => sum + item.instanceCount, 0),
        avgProcessTime: mockData.reduce((sum, item) => sum + item.avgProcessTime, 0) / mockData.length,
      });

      return {
        data: mockData,
        total: mockData.length,
        success: true,
      };
    } catch (error) {
      message.error('获取工作流列表失败');
      return {
        data: [],
        total: 0,
        success: false,
      };
    } finally {
      setLoading(false);
    }
  };

  // 查看工作流详情
  const handleViewDetail = (record: Workflow) => {
    setCurrentWorkflow(record);
    setDetailModalVisible(true);
  };

  // 设计工作流
  const handleDesignWorkflow = (record: Workflow) => {
    navigate(`/system/workflow/design/${record.id}`);
  };

  // 复制工作流
  const handleCopyWorkflow = (record: Workflow) => {
    navigate(`/system/workflow/copy/${record.id}`);
  };

  // 启用/停用工作流
  const handleToggleStatus = async (record: Workflow, checked: boolean) => {
    try {
      const newStatus = checked ? WorkflowStatus.ACTIVE : WorkflowStatus.INACTIVE;
      console.log('切换工作流状态:', record.id, newStatus);
      message.success(checked ? '工作流已启用' : '工作流已停用');
      tableRef.current?.reload();
    } catch (error) {
      message.error('状态更新失败');
    }
  };

  // 删除工作流
  const handleDeleteWorkflow = (record: Workflow) => {
    modal.confirm({
      title: '确认删除工作流',
      content: `确定要删除工作流"${record.name}"吗？删除后无法恢复，正在运行的实例将受到影响。`,
      onOk: async () => {
        try {
          console.log('删除工作流:', record.id);
          message.success('工作流删除成功');
          tableRef.current?.reload();
        } catch (error) {
          message.error('工作流删除失败');
        }
      },
    });
  };

  // 获取工作流状态标签
  const getWorkflowStatusTag = (status: WorkflowStatus) => {
    const statusConfig = {
      [WorkflowStatus.DRAFT]: { color: 'default', text: '草稿', icon: <EditOutlined /> },
      [WorkflowStatus.ACTIVE]: { color: 'success', text: '启用', icon: <PlayCircleOutlined /> },
      [WorkflowStatus.INACTIVE]: { color: 'warning', text: '停用', icon: <PauseCircleOutlined /> },
      [WorkflowStatus.ARCHIVED]: { color: 'error', text: '归档', icon: <CloseCircleOutlined /> },
    };
    const config = statusConfig[status];
    return <Tag color={config.color} icon={config.icon}>{config.text}</Tag>;
  };

  // 获取节点类型标签
  const getNodeTypeTag = (type: NodeType) => {
    const typeConfig = {
      [NodeType.START]: { color: 'green', text: '开始' },
      [NodeType.END]: { color: 'red', text: '结束' },
      [NodeType.APPROVAL]: { color: 'blue', text: '审批' },
      [NodeType.CONDITION]: { color: 'orange', text: '条件' },
      [NodeType.PARALLEL]: { color: 'purple', text: '并行' },
      [NodeType.MERGE]: { color: 'cyan', text: '合并' },
    };
    const config = typeConfig[type];
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  // 操作菜单
  const getActionMenu = (record: Workflow): MenuProps => ({
    items: [
      {
        key: 'view',
        label: '查看详情',
        icon: <EyeOutlined />,
        onClick: () => handleViewDetail(record),
      },
      {
        key: 'design',
        label: '流程设计',
        icon: <NodeIndexOutlined />,
        onClick: () => handleDesignWorkflow(record),
      },
      {
        key: 'copy',
        label: '复制流程',
        icon: <CopyOutlined />,
        onClick: () => handleCopyWorkflow(record),
      },
      {
        key: 'instances',
        label: '查看实例',
        icon: <BranchesOutlined />,
        onClick: () => navigate(`/system/workflow/instances/${record.id}`),
      },
      {
        type: 'divider',
      },
      {
        key: 'export',
        label: '导出配置',
        icon: <ExportOutlined />,
        onClick: () => message.info('导出功能开发中'),
      },
      {
        type: 'divider',
      },
      {
        key: 'delete',
        label: '删除流程',
        icon: <DeleteOutlined />,
        danger: true,
        onClick: () => handleDeleteWorkflow(record),
        disabled: record.status === WorkflowStatus.ACTIVE && record.activeInstanceCount > 0,
      },
    ],
  });

  // 表格列配置
  const columns: ProTableColumn<Workflow>[] = [
    {
      title: '工作流信息',
      dataIndex: 'name',
      key: 'name',
      width: 250,
      fixed: 'left',
      render: (text: string, record: Workflow) => (
        <div>
          <div style={{ fontWeight: 500, marginBottom: '4px' }}>
            <SettingOutlined style={{ marginRight: '4px' }} />
            {record.name}
            {record.isDefault && (
              <Tag color="gold" style={{ marginLeft: '8px' }}>默认</Tag>
            )}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            编码: {record.code} | 版本: {record.version}
          </div>
        </div>
      ),
    },
    {
      title: '分类描述',
      dataIndex: 'category',
      key: 'category',
      width: 200,
      render: (category: string, record: Workflow) => (
        <div>
          <div style={{ marginBottom: '4px' }}>
            <Tag color="blue">{category}</Tag>
          </div>
          <div style={{ fontSize: '12px', color: '#666', lineHeight: 1.4 }}>
            {record.description}
          </div>
        </div>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      align: 'center',
      render: (status: WorkflowStatus, record: Workflow) => (
        <div>
          {getWorkflowStatusTag(status)}
          <div style={{ marginTop: '4px' }}>
            <Switch
              size="small"
              checked={status === WorkflowStatus.ACTIVE}
              onChange={(checked) => handleToggleStatus(record, checked)}
              disabled={status === WorkflowStatus.ARCHIVED}
            />
          </div>
        </div>
      ),
    },
    {
      title: '节点数量',
      dataIndex: 'nodes',
      key: 'nodes',
      width: 100,
      align: 'center',
      render: (nodes: WorkflowNode[]) => (
        <div>
          <div style={{ fontSize: '16px', fontWeight: 500 }}>
            {nodes?.length || 0}
          </div>
          <div style={{ fontSize: '11px', color: '#666' }}>
            个节点
          </div>
        </div>
      ),
    },
    {
      title: '实例统计',
      dataIndex: 'instanceCount',
      key: 'instanceCount',
      width: 120,
      align: 'center',
      render: (instanceCount: number, record: Workflow) => (
        <div>
          <div style={{ marginBottom: '2px' }}>
            总数: <span style={{ fontWeight: 500 }}>{instanceCount}</span>
          </div>
          <div style={{ fontSize: '12px', color: record.activeInstanceCount > 0 ? '#52c41a' : '#666' }}>
            活跃: {record.activeInstanceCount}
          </div>
        </div>
      ),
    },
    {
      title: '平均处理时间',
      dataIndex: 'avgProcessTime',
      key: 'avgProcessTime',
      width: 120,
      align: 'center',
      render: (avgProcessTime: number) => (
        <div>
          <div style={{ fontWeight: 500, color: '#1890ff' }}>
            {avgProcessTime.toFixed(1)}h
          </div>
          <div style={{ fontSize: '11px', color: '#666' }}>
            平均时长
          </div>
        </div>
      ),
    },
    {
      title: '创建信息',
      dataIndex: 'creatorName',
      key: 'creatorName',
      width: 140,
      render: (creatorName: string, record: Workflow) => (
        <div>
          <div style={{ marginBottom: '2px' }}>
            {creatorName}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {record.createdAt.split(' ')[0]}
          </div>
        </div>
      ),
    },
    {
      title: '更新时间',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 160,
      render: (time: string) => (
        <div>
          <div style={{ fontSize: '12px' }}>
            {time.split(' ')[0]}
          </div>
          <div style={{ fontSize: '11px', color: '#666' }}>
            {time.split(' ')[1]}
          </div>
        </div>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      fixed: 'right',
      render: (_, record: Workflow) => (
        <Space size="small">
          <Tooltip title="查看详情">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetail(record)}
            />
          </Tooltip>
          <Tooltip title="流程设计">
            <Button
              type="text"
              size="small"
              icon={<NodeIndexOutlined />}
              onClick={() => handleDesignWorkflow(record)}
            />
          </Tooltip>
          <Dropdown menu={getActionMenu(record)} trigger={['click']}>
            <Button type="text" size="small" icon={<MoreOutlined />} />
          </Dropdown>
        </Space>
      ),
    },
  ];

  // 搜索表单配置
  const searchFormFields = [
    {
      name: 'name',
      label: '工作流名称',
      type: 'input' as const,
      placeholder: '请输入工作流名称',
    },
    {
      name: 'code',
      label: '工作流编码',
      type: 'input' as const,
      placeholder: '请输入工作流编码',
    },
    {
      name: 'category',
      label: '分类',
      type: 'select' as const,
      placeholder: '请选择分类',
      options: [
        { label: '订单管理', value: '订单管理' },
        { label: '采购管理', value: '采购管理' },
        { label: '财务管理', value: '财务管理' },
        { label: '人事管理', value: '人事管理' },
      ],
    },
    {
      name: 'status',
      label: '状态',
      type: 'select' as const,
      placeholder: '请选择状态',
      options: [
        { label: '草稿', value: WorkflowStatus.DRAFT },
        { label: '启用', value: WorkflowStatus.ACTIVE },
        { label: '停用', value: WorkflowStatus.INACTIVE },
        { label: '归档', value: WorkflowStatus.ARCHIVED },
      ],
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: '16px' }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="工作流总数"
              value={workflowStats.totalWorkflows}
              prefix={<SettingOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="启用中"
              value={workflowStats.activeWorkflows}
              prefix={<PlayCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="总实例数"
              value={workflowStats.totalInstances}
              prefix={<BranchesOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="平均处理时间"
              value={workflowStats.avgProcessTime}
              precision={1}
              suffix="小时"
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        {/* 搜索表单 */}
        <SearchForm
          fields={searchFormFields}
          onSearch={setSearchParams}
          onReset={() => setSearchParams({})}
        />

        {/* 表格 */}
        <AdvancedProTable<Workflow>
          ref={tableRef}
          columns={columns}
          request={fetchWorkflowList}
          params={searchParams}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1400 }}
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys,
          }}
          toolbar={{
            title: '审批工作流管理',
            actions: [
              <Button
                key="create"
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate('/system/workflow/create')}
              >
                新建工作流
              </Button>,
            ],
            settings: [
              {
                key: 'reload',
                icon: <ReloadOutlined />,
                tooltip: '刷新',
                onClick: () => tableRef.current?.reload(),
              },
              {
                key: 'export',
                icon: <ExportOutlined />,
                tooltip: '导出',
                onClick: () => message.info('导出功能开发中'),
              },
            ],
          }}
        />
      </Card>

      {/* 工作流详情弹窗 */}
      <Modal
        title="工作流详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            关闭
          </Button>,
          <Button 
            key="design" 
            type="primary" 
            onClick={() => {
              setDetailModalVisible(false);
              handleDesignWorkflow(currentWorkflow!);
            }}
          >
            流程设计
          </Button>,
        ]}
        width={800}
      >
        {currentWorkflow && (
          <div>
            <Descriptions column={2} bordered style={{ marginBottom: '24px' }}>
              <Descriptions.Item label="工作流名称">{currentWorkflow.name}</Descriptions.Item>
              <Descriptions.Item label="工作流编码">{currentWorkflow.code}</Descriptions.Item>
              <Descriptions.Item label="分类">{currentWorkflow.category}</Descriptions.Item>
              <Descriptions.Item label="版本">{currentWorkflow.version}</Descriptions.Item>
              <Descriptions.Item label="状态">{getWorkflowStatusTag(currentWorkflow.status)}</Descriptions.Item>
              <Descriptions.Item label="是否默认">
                {currentWorkflow.isDefault ? (
                  <Tag color="gold">默认流程</Tag>
                ) : (
                  <Tag>非默认</Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="创建人">{currentWorkflow.creatorName}</Descriptions.Item>
              <Descriptions.Item label="节点数量">{currentWorkflow.nodes.length} 个</Descriptions.Item>
              <Descriptions.Item label="实例总数">{currentWorkflow.instanceCount}</Descriptions.Item>
              <Descriptions.Item label="活跃实例">{currentWorkflow.activeInstanceCount}</Descriptions.Item>
              <Descriptions.Item label="平均处理时间">{currentWorkflow.avgProcessTime.toFixed(1)} 小时</Descriptions.Item>
              <Descriptions.Item label="创建时间">{currentWorkflow.createdAt}</Descriptions.Item>
              <Descriptions.Item label="描述" span={2}>
                {currentWorkflow.description}
              </Descriptions.Item>
            </Descriptions>

            {/* 流程节点 */}
            <div>
              <h4>流程节点</h4>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
                {currentWorkflow.nodes.map((node, index) => (
                  <Card
                    key={node.id}
                    size="small"
                    style={{ width: '200px' }}
                    title={
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ 
                          width: '20px', 
                          height: '20px', 
                          borderRadius: '50%', 
                          background: '#1890ff', 
                          color: 'white', 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center', 
                          fontSize: '12px' 
                        }}>
                          {index + 1}
                        </span>
                        {node.name}
                      </div>
                    }
                    extra={getNodeTypeTag(node.type)}
                  >
                    {node.type === NodeType.APPROVAL && (
                      <div>
                        <div style={{ fontSize: '12px', marginBottom: '4px' }}>
                          <strong>审批人:</strong> {node.approverNames.join(', ')}
                        </div>
                        <div style={{ fontSize: '12px', marginBottom: '4px' }}>
                          <strong>审批方式:</strong> {
                            node.approvalType === 'any' ? '任一审批' :
                            node.approvalType === 'all' ? '全部审批' : '顺序审批'
                          }
                        </div>
                        {node.timeLimit && (
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            <strong>时限:</strong> {node.timeLimit} 小时
                          </div>
                        )}
                      </div>
                    )}
                    {node.type === NodeType.CONDITION && node.conditions && (
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        <strong>条件:</strong> {node.conditions}
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default WorkflowList;